import { useCallback, useMemo, useState } from "react";

import {
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Modal,
  ModalProps,
  SimpleGrid,
  Stack,
  StackProps,
  Text,
  TextProps,
  Title,
  TitleProps,
} from "@mantine/core";
import { IconBolt } from "@tabler/icons-react";
import { MantineTabler } from "app/ui/components/mantine-tabler";
import { useSafeDelayedValueOnModalClose } from "app/ui/hooks/useDelayedValue";
import Image, { StaticImageData } from "next/image";

import { HenCalendlySchedulerButton } from "components/HenCalendlySchedulerButton";
import { constrainImageDimensions } from "ui/utils/constrainImageDimensions";

import BenchmarkingToolsUpsellContentGraphic from "./assets/graphic-benchmarking-tools.png";
import ReviewsUpsellContentGraphic from "./assets/graphic-comp-reviews.png";
import EmployeesUpsellContentGraphic from "./assets/graphic-employees.png";
import PayRangesUpsellContentGraphic from "./assets/graphic-pay-ranges.png";
import TotalRewardsUpsellContentGraphic from "./assets/graphic-total-rewards.png";

export type PaidFeatureName =
  | "reviews"
  | "pay-ranges"
  | "total-rewards"
  | "benchmarking"
  | "benchmarking-comptryx"
  | "benchmarking-mercer"
  | "benchmarking-salary-com"
  | "benchmarking-comprehensive"
  | "benchmarking-ecs"
  | "employees"
  | "benchmarking-tools";

export type PaidFeatureModalState =
  | {
      feature: PaidFeatureName;
      opened: true;
    }
  | {
      feature: null;
      opened: false;
    };

const PaidFeatureModalCopy: Partial<
  Record<PaidFeatureName, { title: string; description: string; graphic: StaticImageData }>
> = {
  reviews: {
    title: "Compensation Cycles",
    description:
      "Save time and eliminate mistakes on your next cycle — without a single spreadsheet.",
    graphic: ReviewsUpsellContentGraphic,
  },
  employees: {
    title: "Data Integrations",
    description:
      "Seamlessly connect your HRIS, performance management, and cap table software to get all your critical compensation data in one place.",
    graphic: EmployeesUpsellContentGraphic,
  },
  "pay-ranges": {
    title: "Pay Ranges",
    description: "Store, edit, and selectively share your pay bands from a single source-of-truth.",
    graphic: PayRangesUpsellContentGraphic,
  },
  "total-rewards": {
    title: "Rewards Communications",
    description:
      "Educate employees about their full compensation, including the value of equity and benefits.",
    graphic: TotalRewardsUpsellContentGraphic,
  },
  benchmarking: {
    title: "Premium Benchmarking",
    description: "Access global, multi-industry compensation data from 9M employees.",
    graphic: ReviewsUpsellContentGraphic,
  },
  "benchmarking-tools": {
    title: "Benchmarking Tools",
    description: "Access global, multi-industry compensation data from 9M employees.",
    graphic: BenchmarkingToolsUpsellContentGraphic,
  },
};

export function usePaidFeatureModalDisclosure(): [
  PaidFeatureModalState,
  {
    open: (feature: PaidFeatureName) => void;
    close: () => void;
  },
] {
  const [state, setState] = useState<PaidFeatureModalState>({
    opened: false,
    feature: null,
  });

  const open = useCallback(
    (feature: PaidFeatureName) => {
      setState({ opened: true, feature });
    },
    [setState],
  );

  const close = useCallback(() => {
    setState({ opened: false, feature: null });
  }, [setState]);

  const handlers = useMemo(() => ({ open, close }), [open, close]);

  return [state, handlers];
}

function PfmTitle(props: TitleProps) {
  return <Title order={3} {...props} />;
}

function PfmDesc(props: TextProps) {
  return <Text fz={"sm"} c={"gray.5"} {...props} />;
}

function PfmStack(props: StackProps) {
  return <Stack align={"center"} justify={"center"} ta={"center"} {...props} />;
}

function PfmBadge() {
  return (
    <Badge color="green" sx={{ textTransform: "none" }} size="lg">
      <Group gap={4}>
        <MantineTabler icon={IconBolt} color="yellow.5" size={16} />
        Paid Feature
      </Group>
    </Badge>
  );
}

function PfmGraphic({ staticImageData, alt }: { staticImageData: StaticImageData; alt: string }) {
  return (
    <Box
      mt={32}
      bg="gray.2"
      sx={{
        border: `24px solid var(--mantine-color-gray-2)`,
        borderBottomWidth: 1,
        borderRadius: 12,
      }}
    >
      <Box
        sx={{ borderTopLeftRadius: 12, borderTopRightRadius: 12, overflow: "hidden" }}
        w="100%"
        pos="relative"
      >
        <Image
          src={staticImageData.src}
          {...constrainImageDimensions({
            ...staticImageData,
            newWidth: 270,
          })}
          alt={alt}
        />
      </Box>
    </Box>
  );
}

function PaidFeatureModalInnerContent({ feature }: { feature: PaidFeatureName }) {
  const featureCopy = PaidFeatureModalCopy[feature as keyof typeof PaidFeatureModalCopy];

  if (!feature || !featureCopy) {
    return null;
  }

  return (
    <>
      <PfmStack>
        <PfmBadge />
        <PfmTitle>{featureCopy.title}</PfmTitle>
        <PfmDesc>{featureCopy.description}</PfmDesc>
      </PfmStack>
      <PfmGraphic staticImageData={featureCopy.graphic} alt={featureCopy.title} />
    </>
  );
}

function PaidFeatureModalCtas({ learnMoreLink }: { learnMoreLink: string }) {
  return (
    <Stack gap={24}>
      <Divider color="gray.2" mx={-24} />
      <SimpleGrid cols={2} spacing={12}>
        <Button size="sm" variant="tertiary" component="a" target="_blank" href={learnMoreLink}>
          Learn More
        </Button>
        {/* 0 padding so its not cut off on mobile */}
        <HenCalendlySchedulerButton variant="primary" size="sm" p={0}>
          Schedule a Demo
        </HenCalendlySchedulerButton>
      </SimpleGrid>
    </Stack>
  );
}

function PaidFeatureModalContent({ feature }: { feature: PaidFeatureName }) {
  if (!feature || !PaidFeatureModalCopy[feature as keyof typeof PaidFeatureModalCopy]) {
    return null;
  }
  return <PaidFeatureModalInnerContent feature={feature} />;
}

export function PaidFeatureModal({
  feature: unsafeFeature,
  ...modalProps
}: Omit<ModalProps, "opened"> & PaidFeatureModalState) {
  const feature = useSafeDelayedValueOnModalClose(unsafeFeature);
  const learnMoreLink = (feature ?? "").includes("benchmarking")
    ? "https://www.comprehensive.io/products/benchmarking"
    : "https://www.comprehensive.io/products/compensation-management";

  return (
    <Modal
      {...modalProps}
      size="sm"
      p={"sm"}
      styles={{
        header: {
          // Hide some stuff
          borderBottomWidth: 0,
          marginBottom: `calc(var(--mantine-spacing-xl) * -1)`,
        },
      }}
    >
      <Stack align="center" justify="center" gap={24}>
        <Box mb={32} px={18}>
          <PaidFeatureModalContent feature={feature as PaidFeatureName} />
        </Box>
      </Stack>
      <PaidFeatureModalCtas learnMoreLink={learnMoreLink} />
    </Modal>
  );
}
